/**
 * Franchise card component
 */
import { useTranslation } from 'react-i18next';
import { Franchise } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ProtectedAction } from '@/components/shared/ProtectedAction';
import { canManageFranchise } from '@/lib/permissions';
import { toast } from '@/lib/toast';

interface FranchiseCardProps {
  franchise: Franchise;
  onEdit?: (franchiseId: string) => void;
  onDelete?: (franchiseId: string) => void;
}

export function FranchiseCard({ franchise, onEdit, onDelete }: FranchiseCardProps) {
  const { t } = useTranslation();

  // Generar URL de registro única
  const registrationUrl = `${window.location.origin}/login?franchiseId=${franchise.franchiseId}`;

  const copyUrlToClipboard = () => {
    navigator.clipboard.writeText(registrationUrl);
    toast.success('URL copiada al portapapeles');
  };
  const planColors = {
    free: 'bg-surface-2 text-ink-muted',
    basic: 'bg-info/10 text-info',
    premium: 'bg-accent/10 text-accent',
    enterprise: 'bg-accent/10 text-accent',
  };

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        {franchise.logo ? (
          <img src={franchise.logo} alt={franchise.name} className="w-16 h-16 rounded-lg object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-surface-2 flex items-center justify-center text-2xl">
            🏢
          </div>
        )}
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${planColors[franchise.planTier]}`}>
          {t(`franchises.planTiers.${franchise.planTier}`)}
        </span>
      </div>

      <h3 className="text-lg font-semibold text-ink mb-2">{franchise.name}</h3>

      {franchise.description && (
        <p className="text-sm text-ink-muted mb-3 line-clamp-2">{franchise.description}</p>
      )}

      <div className="space-y-1 text-sm text-ink-subtle mb-4">
        <div className="flex items-center gap-2">
          <span>📧</span>
          <span>{franchise.email}</span>
        </div>
        <div className="flex items-center gap-2">
          <span>📞</span>
          <span>{franchise.phone}</span>
        </div>
        {franchise.website && (
          <div className="flex items-center gap-2">
            <span>🌐</span>
            <a href={franchise.website} target="_blank" rel="noopener noreferrer" className="text-info hover:underline">
              {t('franchises.form.website')}
            </a>
          </div>
        )}
      </div>

      {/* URL de Registro Única */}
      <div className="mt-4 p-3 bg-info/10 rounded-lg border border-info/30">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-info mb-1">
              🔗 URL de Registro para Clientes
            </p>
            <p className="text-xs text-info font-mono break-all">
              {registrationUrl}
            </p>
          </div>
          <button
            onClick={copyUrlToClipboard}
            className="flex-shrink-0 p-2 text-info hover:bg-info/20 rounded-lg transition-colors"
            title="Copiar URL"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-info mt-2">
          💡 Comparte este enlace con tus clientes para que se registren en tu franquicia
        </p>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-hairline mt-4">
        <span className={`text-xs font-medium ${franchise.isActive ? 'text-success' : 'text-danger'}`}>
          {franchise.isActive ? `● ${t('common.active')}` : `● ${t('common.inactive')}`}
        </span>

        <ProtectedAction requiredPermission={(claims) => canManageFranchise(claims, franchise.franchiseId)}>
          <div className="flex gap-2">
            {onEdit && (
              <Button size="sm" variant="ghost" onClick={() => onEdit(franchise.franchiseId)}>
                {t('common.edit')}
              </Button>
            )}
            {onDelete && (
              <Button size="sm" variant="danger" onClick={() => onDelete(franchise.franchiseId)}>
                {t('common.delete')}
              </Button>
            )}
          </div>
        </ProtectedAction>
      </div>
    </Card>
  );
}
